import React, { memo, useEffect, useRef, useState } from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import { CustomSelectContainer, Label, StyledIconButton } from './styles';
import EditIcon from '../../../../assets/icons/EditIcon';
import { CustomSelectProps } from './types';
import { CropConnectLanguages } from '../../../../services/cropConnectApiClient';
import { originalLanguageNames } from '../../../../i18n/locales/languages';

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  onChange,
  options,
  onSave,
  isEditInput = true,
  variant = 'default',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const [hasChanged, setHasChanged] = useState(false);
  const selectRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      const selectElement = selectRef.current?.querySelector(
        '.MuiSelect-select'
      ) as HTMLElement;
      if (selectElement) {
        selectElement.click();
      }
    }
  }, [isEditing]);

  const handleEditClick = () => {
    setIsEditing(true);
    if (selectRef.current) {
      const selectElement = selectRef.current.querySelector(
        '.MuiSelect-select'
      ) as HTMLElement;
      if (selectElement) {
        const mouseDownEvent = new MouseEvent('mousedown', { bubbles: true });
        selectElement.dispatchEvent(mouseDownEvent);
      }
    }
  };

  const handleSaveClick = () => {
    setIsEditing(false);
    if (hasChanged) {
      onSave();
      setHasChanged(false);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);
    onChange(newValue);
    setHasChanged(true);

    if (variant === 'language') {
      handleSaveClick();
    } else {
      setIsEditing(true);
    }

    if (selectRef.current) {
      const selectElement = selectRef.current.querySelector(
        '.MuiSelect-select'
      ) as HTMLElement;
      if (selectElement) {
        selectElement.blur();
      }
    }
  };

  const renderLabel = (option: { value: string; label: string }) => {
    if (variant === 'language') {
      const originalName =
        originalLanguageNames[option.value as CropConnectLanguages];
      return `${originalName} - ${option.label}`;
    }
    return option.label;
  };

  return (
    <CustomSelectContainer isEditing={isEditing}>
      <Label>{label}</Label>
      <FormControl fullWidth>
        <Select
          variant="outlined"
          ref={selectRef}
          value={currentValue}
          onChange={handleSelectChange}
          displayEmpty
          inputProps={{ ref: inputRef }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {renderLabel(option)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isEditInput && variant !== 'language' && (
        <StyledIconButton
          onClick={isEditing ? handleSaveClick : handleEditClick}
        >
          {hasChanged ? (
            <SaveOutlinedIcon style={{ color: '#007EC3' }} />
          ) : (
            <EditIcon />
          )}
        </StyledIconButton>
      )}
    </CustomSelectContainer>
  );
};

export default memo(CustomSelect);
